import * as rxjs from "rxjs";
import { tap, flatMap, mergeMap } from "rxjs/operators";
import { from, forkJoin } from "rxjs";
import { formOperation } from "../../wrappers/formOperation";
import { TBuyerSubmitsNewOfferFormEditable } from "../../../../domain/codecs/formEditable/BuyerSubmitsNewOfferFormEditable";
import { TBuyerUpdatesConveyancerDetailsFormEditable } from "../../../../domain/codecs/formEditable/BuyerUpdatesConveyancerDetailsFormEditable";
import { TBuyerUpdatesMortgageBrokerDetailsFormEditable } from "../../../../domain/codecs/formEditable/BuyerUpdatesMortgageBrokerDetailsFormEditable";
import { TBuyerUpdatesProofOfFundsFormEditable } from "../../../../domain/codecs/formEditable/BuyerUpdatesProofOfFundsFormEditable";
import { TBuyerUpdatesClientDetailsFormEditable } from "../../../../domain/codecs/formEditable/BuyerUpdatesClientDetailsFormEditable";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";
import { TProofOfFundsDocumentFileIO } from "../../../../domain/codecs/fileIO/ProofOfFundsDocumentFileIO";
import { TDeleteProofOfFundsDocumentFormIO } from "../../../../domain/codecs/formIO/DeleteProofOfFundsDocumentFormIO";

export const actions: TActionsDefinitionsList = [
    routeAction("VIEW_BUYER_ENQUIRY", (obs$, lens, set, get) => {
        obs$.pipe(
            tap((a) => set(lens.public_offers_page.data.input.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.new_offer.edited.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.conveyancer_form.edited.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.mortgage_broker_form.edited.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.proof_of_funds_form.edited.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.client_details_form.edited.enquiry_id.set(a.params.enquiryId))),
            tap((a) => set(lens.public_offers_page.proof_of_funds_documents.input.enquiry_id.set(a.params.enquiryId))),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_NEW_OFFER_FORM_CHANGED", (obs$: rxjs.Observable<TBuyerSubmitsNewOfferFormEditable>, lens, set) => {
        obs$.pipe(
            tap((p) => set(lens.public_offers_page.new_offer.set(p))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_NEW_OFFER_SUBMIT", (obs$: rxjs.Observable<TBuyerSubmitsNewOfferFormEditable>, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.public_offers_page.new_offer.status.set("submitting"))),
            flatMap((p) => formOperation("BuyerSubmitsNewOffer", p)),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
            tap(() => set(lens.public_offers_page.new_offer.status.set("success"))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_CONVEYANCER_FORM_CHANGED", (obs$: rxjs.Observable<TBuyerUpdatesConveyancerDetailsFormEditable>, lens, set) => {
        obs$.pipe(
            tap((p) => set(lens.public_offers_page.conveyancer_form.set(p))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_UPDATE_CONVEYANCER_SUBMIT", (obs$: rxjs.Observable<TBuyerUpdatesConveyancerDetailsFormEditable>, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.public_offers_page.conveyancer_form.status.set("submitting"))),
            flatMap((p) => formOperation("BuyerUpdatesConveyancerDetails", p)),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
            tap(() => set(lens.public_offers_page.conveyancer_form.status.set("success"))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_MORTGAGE_BROKER_FORM_CHANGED", (obs$: rxjs.Observable<TBuyerUpdatesMortgageBrokerDetailsFormEditable>, lens, set) => {
        obs$.pipe(
            tap((p) => set(lens.public_offers_page.mortgage_broker_form.set(p))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_UPDATE_MORTGAGE_BROKER_SUBMIT", (obs$: rxjs.Observable<TBuyerUpdatesMortgageBrokerDetailsFormEditable>, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.public_offers_page.mortgage_broker_form.status.set("submitting"))),
            flatMap((p) => formOperation("BuyerUpdatesMortgageBrokerDetails", p)),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
            tap(() => set(lens.public_offers_page.mortgage_broker_form.status.set("success"))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_PROOF_OF_FUNDS_FORM_CHANGED", (obs$: rxjs.Observable<TBuyerUpdatesProofOfFundsFormEditable>, lens, set) => {
        obs$.pipe(
            tap((p) => set(lens.public_offers_page.proof_of_funds_form.set(p))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_UPDATE_PROOF_OF_FUNDS_SUBMIT", (obs$: rxjs.Observable<TBuyerUpdatesProofOfFundsFormEditable>, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.public_offers_page.proof_of_funds_form.status.set("submitting"))),
            flatMap((p) => formOperation("BuyerUpdatesProofOfFunds", p)),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
            tap(() => set(lens.public_offers_page.proof_of_funds_form.status.set("success"))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_CLIENT_DETAILS_FORM_CHANGED", (obs$: rxjs.Observable<TBuyerUpdatesClientDetailsFormEditable>, lens, set) => {
        obs$.pipe(
            tap((p) => set(lens.public_offers_page.client_details_form.set(p))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_UPDATE_CLIENT_DETAILS_SUBMIT", (obs$: rxjs.Observable<TBuyerUpdatesClientDetailsFormEditable>, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.public_offers_page.client_details_form.status.set("submitting"))),
            flatMap((p) => formOperation("BuyerUpdatesClientDetails", p)),
            flatMap(() => formOperation("GetPublicOffersPageOffers", lens.public_offers_page.data.get()(get()))),
            tap((res) => set(lens.public_offers_page.data.set(res))),
            tap(() => set(lens.public_offers_page.client_details_form.status.set("success"))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_LOAD_PROOF_OF_FUNDS_DOCUMENTS", (obs$: rxjs.Observable<string>, lens, set, get) => {
        obs$.pipe(
            tap((enquiryId) => set(lens.public_offers_page.proof_of_funds_documents.input.enquiry_id.set(enquiryId))),
            flatMap(() => formOperation("GetProofOfFundsDocuments", lens.public_offers_page.proof_of_funds_documents.get()(get()))),
            tap((res) => set(lens.public_offers_page.proof_of_funds_documents.set(res))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_UPLOAD_PROOF_OF_FUNDS_DOCUMENT", (obs$: rxjs.Observable<TProofOfFundsDocumentFileIO[]>, lens, set, get) => {
        obs$.pipe(
            mergeMap((files) =>
                forkJoin(...files.map((file) =>
                    from(formOperation("UploadProofOfFundsDocument", file))
                ))
            ),
            flatMap(() => formOperation("GetProofOfFundsDocuments", lens.public_offers_page.proof_of_funds_documents.get()(get()))),
            tap((res) => set(lens.public_offers_page.proof_of_funds_documents.set(res))),
        ).subscribe();
    }),
    action("BUYER_ENQUIRY_DELETE_PROOF_OF_FUNDS_DOCUMENT", (obs$: rxjs.Observable<TDeleteProofOfFundsDocumentFormIO>, lens, set, get) => {
        obs$.pipe(
            flatMap((p) => formOperation("DeleteProofOfFundsDocument", p)),
            flatMap(() => formOperation("GetProofOfFundsDocuments", lens.public_offers_page.proof_of_funds_documents.get()(get()))),
            tap((res) => set(lens.public_offers_page.proof_of_funds_documents.set(res))),
        ).subscribe();
    }),
];
